import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { ArrowLeft, MapPin, Search, Locate, RefreshCw } from 'lucide-react';

import Layout from '../components/Layout';
import { readResponseJSON } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback palette for job types not stored in /api/project-types.
const TYPE_COLOR_FALLBACK = {
  water:        '#3B82F6',  // blue
  mold:         '#10B981',  // green
  fire:         '#EF4444',  // red
  storm:        '#8B5CF6',  // purple
  reconstruction: '#F59E0B', // amber
  asbestos:     '#6B7280',  // grey
  biohazard:    '#EC4899',  // pink
  smoke:        '#374151',  // dark grey
  'bad debt':   '#000000',  // black
};

const DEFAULT_COLOR = '#0EA5E9';

const STATUSES = [
  { key: 'In Progress',         label: 'In Progress' },
  { key: 'Accounts Receivable', label: 'Accounts Receivable' },
];

// Custom DivIcon — a coloured filled circle with white border. Renders
// crisply at every zoom and works without external image assets.
const buildIcon = (color) => L.divIcon({
  className: 'restorepm-map-marker',
  html: `
    <span style="
      display:block;
      width:18px;height:18px;border-radius:50%;
      background:${color};
      border:2px solid #fff;
      box-shadow:0 1px 3px rgba(0,0,0,0.35);
    "></span>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  popupAnchor: [0, -9],
});

// Imperative helper: fit the map's viewport to the given list of
// projects (or do nothing if zero). Lives as a child so it can use the
// react-leaflet `useMap()` hook.
const FitBoundsButton = ({ projects, trigger }) => {
  const map = useMap();
  useEffect(() => {
    if (!projects.length) return;
    const bounds = L.latLngBounds(projects.map((p) => [p.lat, p.lon]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [trigger, projects, map]);
  return null;
};

const MapView = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [notGeocoded, setNotGeocoded] = useState(0);
  const [pendingGeocode, setPendingGeocode] = useState(0);
  const [statusOn, setStatusOn] = useState({ 'In Progress': true, 'Accounts Receivable': true });
  const [typesOn, setTypesOn] = useState({}); // dynamic per available types
  const [typeColors, setTypeColors] = useState({});
  const [search, setSearch] = useState('');
  const [fitTick, setFitTick] = useState(0);
  const mapRef = useRef(null);

  // Initial load + manual refresh. Refresh-only mode keeps existing
  // pins visible (no flash to empty) while we wait for newly geocoded
  // addresses to land.
  const fetchProjects = async (mode = 'initial') => {
    try {
      if (mode === 'initial') setLoading(true);
      if (mode === 'refresh') setRefreshing(true);
      const token = localStorage.getItem('token');
      const [projData, typeData] = await Promise.all([
        fetch(`${API_URL}/api/setup/map/projects`, { headers: { Authorization: `Bearer ${token}` } }).then(readResponseJSON),
        mode === 'initial'
          ? fetch(`${API_URL}/api/project-types`).then(readResponseJSON)
          : Promise.resolve(null),
      ]);
      const list = projData.projects || [];
      setProjects(list);
      setNotGeocoded(projData.not_geocoded || 0);
      setPendingGeocode(projData.pending_geocode || 0);
      if (typeData) {
        const colorMap = {};
        (typeData.project_types || []).forEach((t) => {
          if (t?.color && t.color !== 'FFFFFF') {
            colorMap[t.name] = t.color.startsWith('#') ? t.color : `#${t.color}`;
          }
        });
        list.forEach((p) => {
          const t = (p.job_type || 'Other').trim();
          if (colorMap[t]) return;
          // Tolerate case / whitespace differences between a project's
          // job_type and the configured project-type name so the color set
          // on Setup > Project Types reliably shows on the map.
          const norm = t.toLowerCase();
          const match = Object.keys(colorMap).find((k) => k.trim().toLowerCase() === norm);
          if (match) { colorMap[t] = colorMap[match]; return; }
          const lc = t.toLowerCase();
          colorMap[t] = TYPE_COLOR_FALLBACK[lc] || DEFAULT_COLOR;
        });
        setTypeColors(colorMap);
        const initialTypes = {};
        Array.from(new Set(list.map((p) => (p.job_type || 'Other').trim()))).forEach((t) => {
          initialTypes[t] = true;
        });
        setTypesOn(initialTypes);
      } else {
        // On refresh: merge any newly-discovered job types into the
        // existing checkbox state (default ON) without resetting user's
        // current selections.
        setTypesOn((prev) => {
          const next = { ...prev };
          list.forEach((p) => {
            const t = (p.job_type || 'Other').trim();
            if (!(t in next)) next[t] = true;
          });
          return next;
        });
        setTypeColors((prev) => {
          const next = { ...prev };
          list.forEach((p) => {
            const t = (p.job_type || 'Other').trim();
            if (next[t]) return;
            next[t] = TYPE_COLOR_FALLBACK[t.toLowerCase()] || DEFAULT_COLOR;
          });
          return next;
        });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    let alive = true;
    fetchProjects('initial').then(() => { if (!alive) return; });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh while a background geocode is in flight. We back off as
  // the pending count drops so we don't hammer the API unnecessarily.
  useEffect(() => {
    if (pendingGeocode <= 0 || loading) return undefined;
    // Roughly: enough time for ~5 more addresses to be geocoded (Nominatim
    // is 1 req/sec) before the next refresh.
    const ms = Math.max(5000, Math.min(15000, pendingGeocode * 1100));
    const t = setTimeout(() => { fetchProjects('refresh'); }, ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGeocode, loading]);

  // Filter projects against the current checkbox + search state.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects.filter((p) => {
      if (!statusOn[p.job_status]) return false;
      const type = (p.job_type || 'Other').trim();
      if (typesOn[type] === false) return false;
      if (q) {
        const hay = `${p.customer || ''} ${p.job_location || ''} ${p.address || ''} ${p.project_id || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return Number.isFinite(p.lat) && Number.isFinite(p.lon);
    });
  }, [projects, statusOn, typesOn, search]);

  // Live counts by job type (always reflect status + search filters too)
  const countsByType = useMemo(() => {
    const c = {};
    projects.forEach((p) => {
      if (!statusOn[p.job_status]) return;
      const q = search.trim().toLowerCase();
      if (q) {
        const hay = `${p.customer || ''} ${p.job_location || ''} ${p.address || ''} ${p.project_id || ''}`.toLowerCase();
        if (!hay.includes(q)) return;
      }
      const type = (p.job_type || 'Other').trim();
      c[type] = (c[type] || 0) + 1;
    });
    return c;
  }, [projects, statusOn, search]);

  const typesPresent = useMemo(
    () => Object.keys(typesOn).sort((a, b) => a.localeCompare(b)),
    [typesOn]
  );

  // Initial centre: continental US so the very first paint is sensible
  // even before "fit to markers" kicks in.
  const initialCenter = [39.5, -77];

  const toggleAllTypes = (on) => {
    const next = {};
    typesPresent.forEach((t) => { next[t] = on; });
    setTypesOn(next);
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="mb-4 flex items-center gap-3">
          <Link to="/setup" className="text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 text-sm" data-testid="back-to-setup">
            <ArrowLeft className="w-4 h-4" /> Setup
          </Link>
        </div>
        <div className="mb-6">
          <h2 className="text-lg text-[#5B8DB8] font-light">Administration</h2>
          <h1 className="text-3xl text-gray-700 font-light italic">
            <MapPin className="inline-block w-7 h-7 -mt-1 mr-2 text-[#FFA94D]" />
            Active Projects Map
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Every In-Progress and Accounts-Receivable project, pinned to its job-site address. Coloured by job type.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-4">
          {/* Filters sidebar */}
          <div className="bg-white rounded shadow p-4 space-y-4">
            <div>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search customer, address…"
                  className="w-full pl-7 pr-2 py-1.5 border rounded text-sm"
                  data-testid="map-search"
                />
              </div>
            </div>

            <div>
              <div className="text-xs uppercase text-gray-500 mb-2">Status</div>
              {STATUSES.map((s) => (
                <label key={s.key} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!statusOn[s.key]}
                    onChange={(e) => setStatusOn((p) => ({ ...p, [s.key]: e.target.checked }))}
                    data-testid={`map-status-${s.key.replace(/\s+/g, '-').toLowerCase()}`}
                  />
                  <span>{s.label}</span>
                </label>
              ))}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase text-gray-500">Job Type</span>
                <div className="flex gap-2 text-[11px]">
                  <button type="button" className="text-[#5B8DB8] hover:underline" onClick={() => toggleAllTypes(true)} data-testid="map-types-all">All</button>
                  <button type="button" className="text-[#5B8DB8] hover:underline" onClick={() => toggleAllTypes(false)} data-testid="map-types-none">None</button>
                </div>
              </div>
              {typesPresent.length === 0 && (
                <div className="text-xs text-gray-400">No active projects loaded.</div>
              )}
              {typesPresent.map((t) => {
                const color = typeColors[t] || DEFAULT_COLOR;
                const count = countsByType[t] || 0;
                return (
                  <label key={t} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!typesOn[t]}
                      onChange={(e) => setTypesOn((p) => ({ ...p, [t]: e.target.checked }))}
                      data-testid={`map-type-${t.replace(/\s+/g, '-').toLowerCase()}`}
                    />
                    <span className="inline-block w-3 h-3 rounded-full border border-white shadow" style={{ background: color }} />
                    <span className="flex-1">{t}</span>
                    <span className="text-xs text-gray-400">{count}</span>
                  </label>
                );
              })}
            </div>

            <div className="pt-2 border-t">
              <button
                type="button"
                onClick={() => setFitTick((n) => n + 1)}
                className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm bg-[#FFA94D] hover:bg-[#FF9233] text-white rounded"
                data-testid="map-fit-bounds"
              >
                <Locate className="w-3.5 h-3.5" /> Fit to visible
              </button>
            </div>

            <div className="text-xs text-gray-500 border-t pt-3">
              <div data-testid="map-visible-count"><strong>{filtered.length}</strong> visible / {projects.length} loaded</div>
              {pendingGeocode > 0 && (
                <div className="mt-2 flex items-start gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="map-pending-geocode">
                  <RefreshCw className={`w-3.5 h-3.5 mt-0.5 ${refreshing ? 'animate-spin' : ''}`} />
                  <div className="flex-1">
                    <div><strong>{pendingGeocode}</strong> address{pendingGeocode === 1 ? '' : 'es'} still being geocoded…</div>
                    <div className="text-[11px] text-amber-600 mt-0.5">
                      OpenStreetMap rate-limits us to 1/sec — pins will appear automatically as they finish. First load only; cached after that.
                    </div>
                    <button type="button" onClick={() => fetchProjects('refresh')} disabled={refreshing} className="mt-1 text-[#5B8DB8] hover:underline text-[11px]" data-testid="map-refresh-now">
                      Refresh now
                    </button>
                  </div>
                </div>
              )}
              {notGeocoded > 0 && (
                <div className="mt-1 text-amber-600" title="Addresses Nominatim couldn't resolve">
                  ⚠ {notGeocoded} could not be geocoded
                </div>
              )}
            </div>
          </div>

          {/* Map */}
          <div className="bg-white rounded shadow overflow-hidden" style={{ height: '70vh', minHeight: 500 }}>
            {loading ? (
              <div className="h-full flex items-center justify-center text-gray-500 text-sm">Loading projects & geocoding addresses…</div>
            ) : (
              <MapContainer
                center={initialCenter}
                zoom={5}
                style={{ height: '100%', width: '100%' }}
                ref={mapRef}
                data-testid="map-container"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBoundsButton projects={filtered} trigger={fitTick || (filtered.length ? 1 : 0)} />
                <MarkerClusterGroup chunkedLoading>
                  {filtered.map((p) => {
                    const type = (p.job_type || 'Other').trim();
                    const color = typeColors[type] || DEFAULT_COLOR;
                    return (
                      <Marker key={p.project_id} position={[p.lat, p.lon]} icon={buildIcon(color)}>
                        <Popup>
                          <div className="text-sm">
                            <div className="font-semibold">{p.customer || '(no customer)'}</div>
                            <div className="text-gray-600">{p.job_location || p.address}</div>
                            <div className="mt-1 flex items-center gap-2 text-xs">
                              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                              <span>{type}</span>
                              <span className="text-gray-400">·</span>
                              <span>{p.job_status}</span>
                            </div>
                            {p.pre_tax_total_value && (
                              <div className="text-xs text-gray-600 mt-1">
                                Subtotal: ${parseFloat(String(p.pre_tax_total_value).replace(/[$,]/g, '')).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </div>
                            )}
                            <div className="mt-2">
                              <Link to={`/projects/${p.project_id}`} className="text-[#5B8DB8] hover:underline text-xs" data-testid={`popup-open-${p.project_id}`}>
                                Open project {p.project_id} →
                              </Link>
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MarkerClusterGroup>
              </MapContainer>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MapView;
